import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, Paper, Button, Stack, Chip,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, CircularProgress, Alert, Divider, Table,
    TableBody, TableCell, TableHead, TableRow, Tooltip,
    Switch, FormControlLabel,
} from '@mui/material';
import AddIcon                from '@mui/icons-material/Add';
import EditIcon               from '@mui/icons-material/Edit';
import DeleteIcon             from '@mui/icons-material/Delete';
import ArrowBackIcon          from '@mui/icons-material/ArrowBack';
import SportsMotorsportsIcon  from '@mui/icons-material/SportsMotorsports';
import LockIcon               from '@mui/icons-material/Lock';
import EventSeatIcon          from '@mui/icons-material/EventSeat';
import ShoppingBagIcon        from '@mui/icons-material/ShoppingBag';
import { useAuth }    from '../context/AuthContext';
import axios          from '../api/axiosConfig';
import axiosPublic    from '../api/axiosPublic';
import { formatCurrency } from '../utils/currency';

const TERRACOTTA = '#C4622D';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const WALNUT     = '#2C1F14';

const BIKE_TYPES     = ['NORMAL', 'ELECTRIC', 'ROAD', 'MOUNTAIN', 'GRAVEL'];
const DURATION_TYPES = ['HOURLY', 'DAILY'];

const ADDON_TYPES = ['HELMET', 'LOCK', 'KID_SEAT', 'BAG'];
const ADDON_META  = {
    HELMET:   { label: 'Helmet',   Icon: SportsMotorsportsIcon },
    LOCK:     { label: 'Lock',     Icon: LockIcon              },
    KID_SEAT: { label: 'Kid Seat', Icon: EventSeatIcon         },
    BAG:      { label: 'Bag',      Icon: ShoppingBagIcon       },
};

const emptyForm = {
    bikeType:      'NORMAL',
    durationType:  'HOURLY',
    durationValue: '',
    pricePerUnit:  '',
    depositAmount: '',
};

function centsToDecimal(cents) {
    if (!cents && cents !== 0) return '';
    return (Number(cents) / 100).toFixed(2);
}

function decimalToCents(val) {
    return Math.round(parseFloat(val || 0) * 100);
}

export default function RentalsEdit() {
    const { user, loading: authLoading } = useAuth();
    const navigate   = useNavigate();
    const isManager  = user?.role === 'MANAGER' || user?.role === 'ADMIN';

    const [configs,  setConfigs]  = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [error,    setError]    = useState('');
    const [success,  setSuccess]  = useState('');

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId,  setEditingId]  = useState(null); // null = create
    const [form,       setForm]       = useState(emptyForm);
    const [saving,     setSaving]     = useState(false);
    const [formError,  setFormError]  = useState('');

    // Delete confirm
    const [deleteId,   setDeleteId]   = useState(null);
    const [deleting,   setDeleting]   = useState(false);

    // Addon prices
    const [addonConfigs,    setAddonConfigs]    = useState([]);
    const [addonDialogOpen, setAddonDialogOpen] = useState(false);
    const [addonEditingId,  setAddonEditingId]  = useState(null);
    const [addonForm,       setAddonForm]       = useState({ pricePerUnit: '', mandatory: false });
    const [addonSaving,     setAddonSaving]     = useState(false);
    const [addonFormError,  setAddonFormError]  = useState('');

    useEffect(() => {
        if (!authLoading && !isManager) navigate('/', { replace: true });
    }, [authLoading, isManager, navigate]);

    const fetchConfigs = useCallback(async () => {
        setLoading(true);
        try {
            const [priceRes, addonRes] = await Promise.all([
                axiosPublic.get('/rentals/prices'),
                axiosPublic.get('/rentals/addon-prices'),
            ]);
            setConfigs(priceRes.data || []);
            setAddonConfigs(addonRes.data || []);
        } catch {
            setError('Failed to load price configs.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

    const openAddonEdit = (cfg) => {
        setAddonForm({ pricePerUnit: centsToDecimal(cfg.pricePerUnit), mandatory: cfg.mandatory });
        setAddonEditingId(cfg.id);
        setAddonFormError('');
        setAddonDialogOpen(true);
    };

    const handleAddonSave = async () => {
        if (isNaN(Number(addonForm.pricePerUnit)) || Number(addonForm.pricePerUnit) < 0) {
            setAddonFormError('Price must be a valid non-negative number.');
            return;
        }
        setAddonSaving(true); setAddonFormError('');
        try {
            await axios.put(`/rentals/addon-prices/${addonEditingId}`, {
                pricePerUnit: decimalToCents(addonForm.pricePerUnit),
                mandatory:    addonForm.mandatory,
            });
            setSuccess('Add-on price updated.');
            setAddonDialogOpen(false);
            fetchConfigs();
        } catch (err) {
            setAddonFormError(err?.response?.data?.message || 'Save failed. Please try again.');
        } finally {
            setAddonSaving(false);
        }
    };

    const openCreate = () => {
        setForm(emptyForm);
        setEditingId(null);
        setFormError('');
        setDialogOpen(true);
    };

    const openEdit = (cfg) => {
        setForm({
            bikeType:      cfg.bikeType,
            durationType:  cfg.durationType,
            durationValue: cfg.durationValue ?? '',
            pricePerUnit:  centsToDecimal(cfg.pricePerUnit),
            depositAmount: centsToDecimal(cfg.depositAmount),
        });
        setEditingId(cfg.id);
        setFormError('');
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.durationValue || isNaN(Number(form.durationValue)) || Number(form.durationValue) < 1) {
            setFormError('Duration value must be a positive number.');
            return;
        }
        if (!form.pricePerUnit || isNaN(Number(form.pricePerUnit)) || Number(form.pricePerUnit) < 0) {
            setFormError('Price per unit must be a valid number.');
            return;
        }
        setSaving(true);
        setFormError('');
        try {
            const payload = {
                bikeType:      form.bikeType,
                durationType:  form.durationType,
                durationValue: Number(form.durationValue),
                pricePerUnit:  decimalToCents(form.pricePerUnit),
                depositAmount: decimalToCents(form.depositAmount),
            };
            if (editingId) {
                await axios.put(`/rentals/prices/${editingId}`, payload);
                setSuccess('Price config updated.');
            } else {
                await axios.post('/rentals/prices', payload);
                setSuccess('Price config created.');
            }
            setDialogOpen(false);
            fetchConfigs();
        } catch (err) {
            setFormError(err?.response?.data?.message || 'Save failed. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axios.delete(`/rentals/prices/${deleteId}`);
            setSuccess('Price config deleted.');
            setDeleteId(null);
            fetchConfigs();
        } catch {
            setError('Delete failed.');
        } finally {
            setDeleting(false);
        }
    };

    // Group configs by bikeType
    const grouped = BIKE_TYPES.reduce((acc, bt) => {
        acc[bt] = configs.filter(c => c.bikeType === bt);
        return acc;
    }, {});

    const durationLabel = (cfg) =>
        `${cfg.durationValue ?? '?'} ${cfg.durationType === 'HOURLY' ? 'hr' : 'day'}${cfg.durationValue !== 1 ? 's' : ''}`;

    if (authLoading) return null;
    if (!isManager) return null;

    return (
        <Box sx={{ bgcolor: PARCHMENT, minHeight: '100vh', pb: 8 }}>
            <Container maxWidth="lg" sx={{ py: 4 }}>

                {/* Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between"
                       flexWrap="wrap" gap={2} mb={4}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <IconButton onClick={() => navigate(-1)} sx={{ color: TERRACOTTA }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="overline"
                                        sx={{ color: FOREST, fontWeight: 700, letterSpacing: '0.18em' }}>
                                Manager Tools
                            </Typography>
                            <Typography variant="h4" fontWeight={900}
                                        sx={{ color: WALNUT, lineHeight: 1.1,
                                              fontFamily: "'Playfair Display', Georgia, serif" }}>
                                Rental Price Configs
                            </Typography>
                        </Box>
                    </Stack>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
                            sx={{ background: `linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                                color: 'white', borderRadius: 2, fontWeight: 700, px: 3 }}>
                        New Price Config
                    </Button>
                </Stack>

                {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

                {loading ? (
                    <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>
                ) : (
                    <>
                    <Stack spacing={3}>
                        {BIKE_TYPES.map(bt => {
                            const rows = grouped[bt];
                            return (
                                <Paper key={bt} elevation={0}
                                       sx={{ borderRadius: 3, border: '1px solid rgba(196,98,45,0.15)',
                                             overflow: 'hidden' }}>
                                    {/* Bike type header */}
                                    <Box sx={{ px: 3, py: 1.8,
                                        background: `linear-gradient(135deg, ${WALNUT} 0%, #3E2A1A 100%)`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography fontWeight={800}
                                                    sx={{ color: GOLD, letterSpacing: '0.08em', fontSize: '0.95rem' }}>
                                            {bt}
                                        </Typography>
                                        <Chip label={`${rows.length} config${rows.length !== 1 ? 's' : ''}`}
                                              size="small"
                                              sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)',
                                                    fontWeight: 700, fontSize: '0.72rem' }} />
                                    </Box>

                                    {rows.length === 0 ? (
                                        <Box sx={{ px: 3, py: 2.5, bgcolor: '#fff' }}>
                                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                No price configs defined for {bt} bikes.
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Table size="small" sx={{ bgcolor: '#fff' }}>
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: 'rgba(247,243,238,0.8)' }}>
                                                    {['Duration', 'Type', 'Price / unit', 'Deposit', ''].map(h => (
                                                        <TableCell key={h}
                                                                   sx={{ fontWeight: 800, color: WALNUT,
                                                                         fontSize: '0.75rem',
                                                                         textTransform: 'uppercase',
                                                                         letterSpacing: '0.06em',
                                                                         borderBottom: '1px solid rgba(196,98,45,0.15)' }}>
                                                            {h}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rows.map(cfg => (
                                                    <TableRow key={cfg.id}
                                                              sx={{ '&:hover': { bgcolor: 'rgba(196,98,45,0.04)' } }}>
                                                        <TableCell sx={{ fontWeight: 700, color: WALNUT }}>
                                                            {durationLabel(cfg)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={cfg.durationType} size="small"
                                                                  sx={{ bgcolor: cfg.durationType === 'HOURLY'
                                                                      ? 'rgba(196,98,45,0.1)' : 'rgba(74,103,65,0.1)',
                                                                      color: cfg.durationType === 'HOURLY' ? TERRACOTTA : FOREST,
                                                                      fontWeight: 700, fontSize: '0.7rem' }} />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: FOREST }}>
                                                            {formatCurrency(Number(cfg.pricePerUnit), 'EUR')}
                                                        </TableCell>
                                                        <TableCell sx={{ color: 'text.secondary' }}>
                                                            {Number(cfg.depositAmount) > 0
                                                                ? formatCurrency(Number(cfg.depositAmount), 'EUR')
                                                                : '—'}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                                <IconButton size="small"
                                                                            onClick={() => openEdit(cfg)}
                                                                            sx={{ color: TERRACOTTA }}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton size="small"
                                                                            onClick={() => setDeleteId(cfg.id)}
                                                                            sx={{ color: '#c62828' }}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </Paper>
                            );
                        })}
                    </Stack>

                    {/* ── Add-on Prices ── */}
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" fontWeight={800}
                                    sx={{ color: WALNUT, mb: 2,
                                          fontFamily: "'Playfair Display', Georgia, serif" }}>
                            Add-on Prices
                        </Typography>
                        <Paper elevation={0}
                               sx={{ borderRadius: 3, border: '1px solid rgba(196,98,45,0.15)',
                                     overflow: 'hidden' }}>
                            <Box sx={{ px: 3, py: 1.8,
                                background: `linear-gradient(135deg, ${WALNUT} 0%, #3E2A1A 100%)`,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography fontWeight={800}
                                            sx={{ color: GOLD, letterSpacing: '0.08em', fontSize: '0.95rem' }}>
                                    EQUIPMENT ADD-ONS
                                </Typography>
                                <Chip label="4 types" size="small"
                                      sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)',
                                            fontWeight: 700, fontSize: '0.72rem' }} />
                            </Box>
                            <Table size="small" sx={{ bgcolor: '#fff' }}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'rgba(247,243,238,0.8)' }}>
                                        {['Equipment', 'Price / unit', 'Mandatory', ''].map(h => (
                                            <TableCell key={h}
                                                       sx={{ fontWeight: 800, color: WALNUT,
                                                             fontSize: '0.75rem',
                                                             textTransform: 'uppercase',
                                                             letterSpacing: '0.06em',
                                                             borderBottom: '1px solid rgba(196,98,45,0.15)' }}>
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {addonConfigs
                                        .sort((a, b) => ADDON_TYPES.indexOf(a.equipmentType) - ADDON_TYPES.indexOf(b.equipmentType))
                                        .map(cfg => {
                                            const meta = ADDON_META[cfg.equipmentType];
                                            const Icon = meta?.Icon;
                                            return (
                                                <TableRow key={cfg.id}
                                                          sx={{ '&:hover': { bgcolor: 'rgba(196,98,45,0.04)' } }}>
                                                    <TableCell>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            {Icon && <Icon sx={{ fontSize: '1rem', color: FOREST }} />}
                                                            <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>
                                                                {meta?.label || cfg.equipmentType}
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: FOREST }}>
                                                        {formatCurrency(Number(cfg.pricePerUnit), 'EUR')}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={cfg.mandatory ? 'Yes' : 'No'} size="small"
                                                              sx={{ bgcolor: cfg.mandatory
                                                                      ? 'rgba(74,103,65,0.1)' : 'rgba(196,98,45,0.08)',
                                                                    color: cfg.mandatory ? FOREST : TERRACOTTA,
                                                                    fontWeight: 700, fontSize: '0.7rem' }} />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton size="small"
                                                                    onClick={() => openAddonEdit(cfg)}
                                                                    sx={{ color: TERRACOTTA }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Box>
                    </>
                )}
            </Container>

            {/* Create / Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)}
                    maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={800}
                             sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {editingId ? 'Edit Price Config' : 'New Price Config'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 0.5 }}>
                        {/* Bike type */}
                        <TextField select fullWidth size="small" label="Bike Type"
                                   value={form.bikeType}
                                   onChange={e => setForm(f => ({ ...f, bikeType: e.target.value }))}>
                            {BIKE_TYPES.map(bt => (
                                <MenuItem key={bt} value={bt}>{bt}</MenuItem>
                            ))}
                        </TextField>

                        {/* Duration type */}
                        <Box>
                            <Typography variant="caption" fontWeight={700} color="text.secondary"
                                        sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.8 }}>
                                Duration Type
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                {DURATION_TYPES.map(dt => (
                                    <Chip key={dt} label={dt}
                                          onClick={() => setForm(f => ({ ...f, durationType: dt }))}
                                          variant={form.durationType === dt ? 'filled' : 'outlined'}
                                          sx={{
                                              fontWeight: 700,
                                              bgcolor:    form.durationType === dt ? TERRACOTTA : 'white',
                                              color:      form.durationType === dt ? 'white' : TERRACOTTA,
                                              borderColor: form.durationType === dt ? TERRACOTTA : 'rgba(196,98,45,0.4)',
                                              '&:hover':  { bgcolor: form.durationType === dt ? '#9A4A1F' : 'rgba(196,98,45,0.08)' },
                                          }} />
                                ))}
                            </Stack>
                        </Box>

                        {/* Duration value */}
                        <TextField fullWidth size="small" type="number"
                                   label={form.durationType === 'HOURLY' ? 'Hours' : 'Days'}
                                   value={form.durationValue}
                                   inputProps={{ min: 1, step: 1 }}
                                   onChange={e => setForm(f => ({ ...f, durationValue: e.target.value }))} />

                        <Divider />

                        {/* Price */}
                        <TextField fullWidth size="small" type="number" label="Price per unit (€)"
                                   value={form.pricePerUnit}
                                   inputProps={{ min: 0, step: '0.01', placeholder: 'e.g. 5.00' }}
                                   onChange={e => setForm(f => ({ ...f, pricePerUnit: e.target.value }))} />

                        {/* Deposit */}
                        <TextField fullWidth size="small" type="number" label="Deposit amount (€)"
                                   value={form.depositAmount}
                                   inputProps={{ min: 0, step: '0.01', placeholder: 'e.g. 20.00' }}
                                   onChange={e => setForm(f => ({ ...f, depositAmount: e.target.value }))} />

                        {formError && <Alert severity="error" sx={{ borderRadius: 2 }}>{formError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}
                            sx={{ background: `linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                                color: 'white', borderRadius: 2, fontWeight: 700 }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : (editingId ? 'Save Changes' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm Dialog */}
            <Dialog open={!!deleteId} onClose={() => !deleting && setDeleteId(null)}
                    maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={700} sx={{ color: '#b71c1c' }}>Delete Price Config</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to delete this price config? This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setDeleteId(null)} disabled={deleting}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
                            sx={{ borderRadius: 2, fontWeight: 700 }}>
                        {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add-on Edit Dialog */}
            <Dialog open={addonDialogOpen} onClose={() => !addonSaving && setAddonDialogOpen(false)}
                    maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={800}
                             sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                    Edit Add-on Price
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 0.5 }}>
                        {(() => {
                            const cfg  = addonConfigs.find(c => c.id === addonEditingId);
                            const meta = cfg ? ADDON_META[cfg.equipmentType] : null;
                            const Icon = meta?.Icon;
                            return cfg ? (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    {Icon && <Icon sx={{ fontSize: '1.2rem', color: FOREST }} />}
                                    <Typography fontWeight={700} sx={{ color: WALNUT }}>
                                        {meta?.label || cfg.equipmentType}
                                    </Typography>
                                </Stack>
                            ) : null;
                        })()}
                        <Divider />
                        <TextField fullWidth size="small" type="number" label="Price per unit (€)"
                                   value={addonForm.pricePerUnit}
                                   inputProps={{ min: 0, step: '0.01', placeholder: 'e.g. 2.00' }}
                                   onChange={e => setAddonForm(f => ({ ...f, pricePerUnit: e.target.value }))} />
                        <FormControlLabel
                            control={
                                <Switch checked={addonForm.mandatory}
                                        onChange={e => setAddonForm(f => ({ ...f, mandatory: e.target.checked }))}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: FOREST },
                                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: FOREST } }} />
                            }
                            label={
                                <Tooltip title="Mandatory add-ons are always included; customers cannot opt out.">
                                    <Typography variant="body2" fontWeight={600}>Mandatory</Typography>
                                </Tooltip>
                            }
                        />
                        {addonFormError && <Alert severity="error" sx={{ borderRadius: 2 }}>{addonFormError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setAddonDialogOpen(false)} disabled={addonSaving}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddonSave} disabled={addonSaving}
                            sx={{ background: `linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                                color: 'white', borderRadius: 2, fontWeight: 700 }}>
                        {addonSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
